import { useState, useEffect, useCallback } from 'react';
import { Voucher, CreateVoucherData } from '../types/voucher.types';
import dataProvider from '../../services/api';

interface ErrorHandlers {
  handleErrorWithLogout: (error: any) => void;
  showSuccessMessage: (message: string) => void;
  showErrorMessage: (message: string) => void;
}

export const useVouchers = (debouncedSearchTerm: string, errorHandlers: ErrorHandlers) => {
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVoucher, setSelectedVoucher] = useState<number>(0);
  const [isCreating, setIsCreating] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { handleErrorWithLogout, showSuccessMessage, showErrorMessage } = errorHandlers;

  const fetchVouchers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await dataProvider.getVouchers({
        filter: debouncedSearchTerm,
        page: 0,
        count: 100
      });
      setVouchers(result.data);
      
      if (result.data.length > 0) {
        const currentExists = result.data.some((v: Voucher) => v.voucherId === selectedVoucher);
        if (selectedVoucher === 0 || !currentExists) {
          setSelectedVoucher(result.data[0].voucherId);
        }
      } else {
        setSelectedVoucher(0);
      }
    } catch (err) {
      setError('Failed to fetch vouchers');
      handleErrorWithLogout(err);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm]);

  useEffect(() => {
    fetchVouchers();
  }, [fetchVouchers]);

  const createVoucher = useCallback(async (voucherData: CreateVoucherData) => {
    try {
      setIsCreating(true);
      await dataProvider.createVoucher(voucherData);
      showSuccessMessage('Voucher created successfully!');
      await fetchVouchers();
    } catch (err) {
      handleErrorWithLogout(err);
      throw err;
    } finally {
      setIsCreating(false);
    }
  }, []);

  const toggleVoucherStatus = useCallback(async (voucher: Voucher) => {
    try {
      setIsToggling(true);
      
      const updatedVoucherData = {
        voucherId: voucher.voucherId,
        type: parseInt(voucher.type),
        amount: voucher.amount,
        cap: voucher.cap,
        limit: voucher.limit,
        isActive: !voucher.isActive,
      };

      await dataProvider.updateVoucher(updatedVoucherData);
      
      const statusText = voucher.isActive ? 'disabled' : 'activated';
      showSuccessMessage(`Voucher ${statusText} successfully!`);
      
      await fetchVouchers();
    } catch (err) {
      handleErrorWithLogout(err);
    } finally {
      setIsToggling(false);
    }
  }, []);

  const saveVoucher = useCallback(async (voucher: Voucher) => {
    try {
      setIsSaving(true);

      const voucherUpdateData = {
        voucherId: voucher.voucherId,
        type: parseInt(voucher.type),
        amount: voucher.amount,
        cap: voucher.cap,
        limit: voucher.limit,
        isActive: voucher.isActive,
      };

      const cityAssignmentData = {
        voucherId: voucher.voucherId,
        cityIds: voucher.cityIds,
      };

      await Promise.all([
        dataProvider.updateVoucher(voucherUpdateData),
        dataProvider.assignCities(cityAssignmentData)
      ]);

      showSuccessMessage('Voucher updated and cities assigned successfully.');
      await fetchVouchers();
    } catch (err) {
      handleErrorWithLogout(err);
    } finally {
      setIsSaving(false);
    }
  }, []);

  const updateVoucherField = useCallback((field: string, value: string | number) => {
    const currentVoucher = vouchers.find(v => v.voucherId === selectedVoucher);
    if (currentVoucher) {
      const updatedVouchers = vouchers.map(v => 
        v.voucherId === currentVoucher.voucherId 
          ? { ...v, [field]: value }
          : v
      );
      setVouchers(updatedVouchers);
    }
  }, [vouchers, selectedVoucher]);

  const cancelChanges = useCallback(async () => {
    await fetchVouchers();
    showSuccessMessage('Changes cancelled successfully');
  }, []);

  const retryFetch = useCallback(async () => {
    setError(null);
    await fetchVouchers();
    showSuccessMessage('Vouchers loaded successfully');
  }, []);

  return {
    vouchers,
    setVouchers,
    loading,
    error,
    selectedVoucher,
    setSelectedVoucher,
    isCreating,
    isToggling,
    isSaving,
    createVoucher,
    toggleVoucherStatus,
    saveVoucher,
    updateVoucherField,
    cancelChanges,
    retryFetch,
    refreshVouchers: fetchVouchers
  };
};